import { Injectable } from '@angular/core';
import {AuthService} from "./auth.service";
import {firstValueFrom, Observable, of, timer} from "rxjs";
import {Game} from "../model/broadcast/game";
import {Broadcast, ManualData} from "../model/broadcast/broadcast";
import {User} from "../model/user";
import {catchError, map, take, timeout} from "rxjs/operators";
import {Control, ControlAction, ControlComponent} from "../model/broadcast/control";
import {RostersService} from "./rosters.service";
import {forkJoin} from "rxjs";
import {deleteDoc, query, Timestamp, where} from "@angular/fire/firestore";
import {League, TeamDefaultConfig} from "../model/league";
import {Player} from "../model/broadcast/player";
import {collection, collectionData, doc, docData, Firestore, setDoc, updateDoc} from "@angular/fire/firestore";
import {LeaguesService} from "./leagues.service";
import {UtilService} from "./util.service";

// A roster read that has not answered by now is treated as absent. Long enough
// that a slow connection still gets its players, short enough that the operator
// is not left staring at a dialog before a game.
const ROSTER_LOAD_TIMEOUT_MS = 10000;

@Injectable({
  providedIn: 'root'
})
export class BroadcastsService {

  private allBroadcastsRef: any;
  private ownerUid: string;

  constructor(
    private readonly firestore: Firestore,
    private authService: AuthService,
    private rostersService: RostersService,
    private leagueService: LeaguesService,
    private utilService: UtilService,
  ) {
    this.allBroadcastsRef = collection(this.firestore, `broadcasts`);
    authService.user$.subscribe(user => {
      if (user) {
        this.ownerUid = user.uid;
      }
    });
  }

  getBroadcast(uid: string): Observable<Broadcast> {
    const documentReference = doc(this.firestore, `broadcasts/${uid}`);
    return docData(documentReference) as Observable<Broadcast>;
  }

  getLinkedBroadcastUid(userUid?: string): Observable<string | null> {
    if (!userUid) {
      userUid = this.authService?.user?.uid;
    }
    if (!userUid) {
      return of(null);
    }
    return docData(doc(this.firestore, `users/${userUid}`)).pipe(map((user: User | undefined) => {
      return user?.linkedBroadcastUid
    }));
  }

  linkBroadcastToUserOutput(broadcastUid: string): Promise<void> {
    return updateDoc(this.authService.userRef, {linkedBroadcastUid: broadcastUid});
  }

  getMyBroadcasts(): Observable<Broadcast[]> {
    return this.getBroadcastsByOwner(this.ownerUid);
  }

  getBroadcastsByOwner(ownerUid: string): Observable<Broadcast[]> {
    const ownerQuery = query(this.allBroadcastsRef, where('ownerUid', '==', ownerUid));
    return collectionData(ownerQuery) as Observable<Broadcast[]>;
  }

  changeOwner(broadcastUid: string, newOwnerUid: string): Promise<void> {
    const ref = doc(this.allBroadcastsRef, broadcastUid);
    return updateDoc(ref, {ownerUid: newOwnerUid});
  }

  newGuid(): string {
    return doc(this.allBroadcastsRef).id;
  }

  updateControl(broadcastUid: string, control: Control): Promise<any> {
    const controlDocRef = doc(
      this.firestore,
      'broadcasts',
      broadcastUid,
      'controls',
      control.component
    );
    if (control.component === 'partnerLogos' && control.action === 'off') {
      control.index++;
    }
    const cleanControl: any = {};
    Object.entries(control).forEach(([key, value]) => {
      if (value !== undefined) cleanControl[key] = value;
    });
    return setDoc(controlDocRef, cleanControl);
  }

  getControls(broadcastUid: string): Observable<Control[]> {
    return collectionData(collection(this.firestore, 'broadcasts', broadcastUid, 'controls')) as Observable<Control[]>;
  }

  public getControlAction(controls: Control[], component: ControlComponent, singleComponent?: string): ControlAction {
    if (!singleComponent || singleComponent == 'main' &&
      ['status', 'intro', 'awayLineup', 'homeLineup', 'awayDefense', 'homeDefense'].includes(component)) {
      const control = controls?.find(c => c.component === component);
      if (control) {
        return control.action;
      } else {
        return 'off';
      }
    } else {
      if (singleComponent != component) {
        return 'off';
      }
    }
  }

  public getControl(controls: Control[], component: ControlComponent) {
    return controls?.find(c => c.component === component);
  }

  updateConfig(broadcast: Broadcast): Promise<any> {
    const ref = doc(this.allBroadcastsRef, broadcast.uid);
    const promiseConfig = updateDoc(ref, {config: broadcast.config});
    const promiseGameId = updateDoc(ref, {gameId: broadcast.gameId});
    return Promise.all([promiseGameId, promiseConfig]);
  }

  async createBroadcast(league: League, home: TeamDefaultConfig, away: TeamDefaultConfig, gameId : string, autopilot?: boolean, scheduled?: Timestamp): Promise<string> {
    const ref = doc(this.allBroadcastsRef);
    let uid = ref.id;
    const broadcast: Broadcast = {
      config: {
        unites: 'MPH',
        lowerRange: false,
        autopilot: autopilot,
        boxScoreTime: 20,
        batterTime: 8,
        manualStatus: !gameId,
        useManualLineups: false,
        homeConfig: home,
        awayConfig: away,
        smallLogoUrl: league ? league.smallLogoUrl : '/assets/logos/ball-score/1h105.png',
        bigLogoUrl: league? league.bigLogoUrl : '',
        backgroundColor: league ? league.backgroundColor : '#212121cc',
      },
      gameId: gameId + '',
      leagueId: league? league.id : '',
      ownerUid: this.ownerUid,
      uid,
      fullRosters: [],
      customTable: {
        title: 'umpires',
        width: 500,
        data: [
          {label: 'UIC: ', value: ''},
          {label: 'U1: ', value: ''},
          {label: 'U3: ', value: ''},
        ]
      }
    };
    if (scheduled) {
      broadcast.scheduled = scheduled;
    }
    //todo rozmyslet a dodelat
    //broadcast.manualData = this.utilService.parseToManualData(broadcast.game);
    broadcast.manualData = {
      away: 0,
      awayPitches: 0,
      balls: 0,
      first: false,
      home: 0,
      homePitches: 0,
      index: 0,
      inning: 1,
      inningPart: 'T',
      outs: 0,
      second: false,
      strikes: 0,
      third: false
    };
    const isLicenced = this.utilService.isLicencedForBroadcast(league, home.id);
    if (league && isLicenced) {
      // The rosters are read BEFORE the document is written, so they go in with
      // the single setDoc below. They used to be filled in afterwards, by a
      // fire-and-forget updateDoc from inside a subscribe that raced the setDoc
      // creating the document — and when it lost, the broadcast kept an empty
      // fullRosters for good, with nothing logged and no way to reimport from
      // the UI. Seen in prod on 27.9.2026: of three broadcasts for game 209965,
      // the 07:05 one got all 32 players and the 08:42 one none.
      broadcast.fullRosters = await this.loadRosters(league.id, home.id, away.id);
      league.availableComponents.forEach(c => {
        setDoc(doc(ref,'controls', c), {action: 'off', component: c, index: 0});
      });
    } else {
      setDoc(doc(ref,'controls', 'status'), {action: 'off', component: 'status', index: 0});
    }
    await setDoc(ref, broadcast);
    return uid;
  }

  // Both teams' rosters out of leagues/{leagueId}/rosters, as one array.
  //
  // Never lets a roster read stop a broadcast from being created: an empty
  // roster is a nuisance the operator can repair from the roster dialog, a
  // broadcast that never appears is a lost game. A missing roster document
  // already resolves to an empty array (RostersService), so what is caught here
  // is a failing or hanging read — hence the timeout as well as the catch.
  loadRosters(leagueId: string, homeId: string, awayId: string): Promise<Player[]> {
    return firstValueFrom(
      forkJoin([
        this.rostersService.getRoster(leagueId, homeId).pipe(take(1)),
        this.rostersService.getRoster(leagueId, awayId).pipe(take(1)),
      ]).pipe(
        timeout(ROSTER_LOAD_TIMEOUT_MS),
        map(([homeRoster, awayRoster]) => [...homeRoster, ...awayRoster]),
        catchError(err => {
          console.error('roster load failed, continuing without it', err);
          return of([] as Player[]);
        }),
      )
    );
  }

  detectPowerBy(broadcast: Broadcast, updatedGame: Game, oldGame: Game) {
    if (!broadcast || !updatedGame || !oldGame) return;

    if (updatedGame?.inning === oldGame?.inning && updatedGame?.inningPart == oldGame?.inningPart
      && (updatedGame?.inningPart === 'T' || updatedGame?.inningPart === 'B')
      && updatedGame?.outs != oldGame?.outs && oldGame?.outs === 0) {
      this.toggleForTime(broadcast, 'poweredBy', 7, 2);
    }
  }

  detectChanges(broadcast: Broadcast, updatedGame: Game, oldGame: Game) {
    if (!broadcast || !updatedGame || !oldGame) return;

    if (updatedGame.finished && !oldGame.finished) {
      this.updateControl(broadcast.uid, {action: 'off', component: 'status'});
      this.updateControl(broadcast.uid, {action: 'off', component: 'poweredBy'});
      this.updateControl(broadcast.uid, {action: 'off', component: 'batter'});
      this.updateControl(broadcast.uid, {action: 'off', component: 'lowerThird'});
      this.updateControl(broadcast.uid, {action: 'off', component: 'customTable'});
      this.updateControl(broadcast.uid, {action: 'off', component: 'relatedGames'});
      this.updateControl(broadcast.uid, {action: 'on', component: 'boxScore'});
      return;
    }

    if (updatedGame.currentBatter.guid != oldGame.currentBatter?.guid && updatedGame.inningPart != 'M' && updatedGame.inningPart != 'E') {
      this.toggleForTime(broadcast, 'batter', broadcast.config.batterTime);
    }

    if ((oldGame.inningPart === 'M' && updatedGame.inningPart === 'B') || (oldGame.inningPart === 'E' && updatedGame.inningPart === 'T')) {
      this.updateControl(broadcast.uid, {action: 'on', component: 'status'});
      this.updateControl(broadcast.uid, {action: 'off', component: 'boxScore'});
      this.toggleForTime(broadcast, 'batter', broadcast.config.batterTime, 5);
    }

    if (updatedGame.inningPart === 'M' || updatedGame.inningPart === 'E') {
      this.toggleForTime(broadcast, 'boxScore', broadcast.config.boxScoreTime);
    }
  }

  public toggleForTime(broadcast: Broadcast, component: ControlComponent, time: number, delay?: number) {
    timer(delay * 1000).subscribe(delay => {
      console.log('showing ' + component);
      this.updateControl(broadcast.uid, {action: 'on', component}).then(resp => {
        timer(time * 1000).subscribe(time => {
          console.log('hiding ' + component);
          this.updateControl(broadcast.uid, {action: 'off', component})
        });
      });
    });
  }

  deleteBroadcast(broadcast: Broadcast): Promise<void> {
    return deleteDoc(doc(this.allBroadcastsRef, broadcast.uid));
  }

  updateManualData(broadcast: Broadcast): Promise<void> {
    if(!broadcast.manualData.index) {
      broadcast.manualData.index = 1;
    } else {
      broadcast.manualData.index++;
    }
    const ref = doc(this.allBroadcastsRef, broadcast.uid);
    const refHistory = doc(this.allBroadcastsRef, broadcast.uid, 'manual-data-history', broadcast.manualData.index.toString());
    const histPromise = setDoc(refHistory, broadcast.manualData);
    const dataPromise = updateDoc(ref, {manualData: broadcast.manualData});
    return Promise.all([histPromise, dataPromise]).then(() => {});
  }

  getManualDataHistory(broadcastUid: string): Observable<ManualData[]> {
    const refHistory = collection(this.allBroadcastsRef, broadcastUid, '/manual-data-history');
    return collectionData(refHistory) as Observable<ManualData[]>;
  }

  updateRadarSpeed(broadcast: Broadcast): Promise<void> {
    const ref = doc(this.allBroadcastsRef, broadcast.uid);
    return updateDoc(ref, {radarSpeed: broadcast.radarSpeed}).finally(() => {
      if (broadcast.radarSpeed) {
        timer(2500).pipe(take(1)).subscribe(t => {
          broadcast.radarSpeed = null;
          return this.updateRadarSpeed(broadcast);
        });
      }
    });
  }

  resettingManualData(broadcast: Broadcast, resetting: boolean): Promise<void> {
    const ref = doc(this.allBroadcastsRef, broadcast.uid);
    return updateDoc(ref, {resettingManualData: resetting});
  }

  updateRosters(broadcast: Broadcast): Promise<void> {
    const ref = doc(this.allBroadcastsRef, broadcast.uid);
    return updateDoc(ref, {fullRosters: broadcast.fullRosters});
  }

  updateRosterWithSync(broadcast: Broadcast): Promise<any> {
    const writes: Promise<any>[] = [this.updateRosters(broadcast)];
    if (broadcast.manualLineups) {
      writes.push(this.updateManualLineups(broadcast));
    }
    if (broadcast.lowerThird) {
      writes.push(this.updateLowerThird(broadcast));
    }
    return Promise.all(writes);
  }

  updateLowerThird(broadcast: Broadcast) {
    const ref = doc(this.allBroadcastsRef, broadcast.uid);
    return updateDoc(ref, {lowerThird: broadcast.lowerThird});
  }

  updateManualLineups(broadcast: Broadcast) {
    const ref = doc(this.allBroadcastsRef, broadcast.uid);
    return updateDoc(ref, {manualLineups: broadcast.manualLineups});
  }

  updateCustomTable(broadcast: Broadcast) {
    const ref = doc(this.allBroadcastsRef, broadcast.uid);
    return updateDoc(ref, {customTable: broadcast.customTable});
  }
}
